/**
 * usePolicySemanticSearch — the Policies-page Volltext/Semantisch toggle
 * (dms/spec.md §5.2, §M10, §M12).
 *
 * WEB-1 fix (wave dms-fixC): BOTH toggle positions call this hook now.
 * Volltext used to stay a CLIENT-SIDE `usePolicyRows` substring filter that
 * could never find a policy by its PDF CONTENT; it now fires the SAME
 * `/search` request as Semantisch whenever it has a non-empty query
 * (`PoliciesScreen`'s `showHitList` derivation decides exactly when — an
 * empty Volltext query still shows the plain grid, unfiltered). `scope:
 * 'policy'` (M10) — only 'police'-typed hits come back, ranked by the api's
 * scoring bands (identifier keyword hits still outrank content/semantic
 * ones there — `searchPolicyScope` runs both legs together server-side, so
 * there's no separate keyword-only mode to give Volltext instead). Debounced
 * the same way `useSearch` debounces the global search bar.
 *
 * live mode → GET /search?scope=policy via fetchPolicySemanticSearch.
 * mock mode → mockPolicySearch(q) (deterministic, zero network).
 *
 * [Source: apps/web/src/features/search/hooks/useSearch.ts] (debounce + live/mock precedent)
 * [Source: projects/broker-ag/dms/spec.md §5.2, §M12]
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SearchResponseDto } from '@brokly/shared';
import { qk } from '../../../lib/query-keys';
import { isLiveData } from '../../../lib/data-mode';
import { fetchPolicySemanticSearch, mockPolicySearch } from '../../search/api/search-api';

/** Minimum query length before a semantic search fires (mirrors SEARCH_MIN_QUERY_LENGTH). */
export const POLICY_SEMANTIC_MIN_QUERY_LENGTH = 2;

/** Debounce window (ms) — the useSearch precedent. */
export const POLICY_SEMANTIC_DEBOUNCE_MS = 250;

function useDebounced<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

/** Run the debounced policy-scoped semantic search (§4.3's "Semantisch" mode). */
export function usePolicySemanticSearch(rawQuery: string, enabled: boolean) {
  const debounced = useDebounced(rawQuery.trim(), POLICY_SEMANTIC_DEBOUNCE_MS);
  const live = isLiveData();
  const active = enabled && debounced.length >= POLICY_SEMANTIC_MIN_QUERY_LENGTH;

  const query = useQuery<SearchResponseDto>({
    queryKey: qk.policySemanticSearch(debounced),
    queryFn: () =>
      live ? fetchPolicySemanticSearch(debounced) : Promise.resolve(mockPolicySearch(debounced)),
    enabled: active,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });

  return {
    hits: active ? (query.data?.items ?? []) : [],
    isLoading: active && query.isLoading,
    isError: active && query.isError,
    searched: active,
    // S4: the semantic leg degrades silently when the embed service is down - the
    // response says so and the screen must be able to say it too. Only false once a
    // response actually carried semantic_available: false; an absent field (mock mode,
    // older api) is not a degradation.
    semanticAvailable: query.data?.semantic_available !== false,
  };
}
